'use client';
import React, { useState, useEffect } from 'react';
import { entryService, userService } from '@/lib/supabaseService';
import { CLIMBS, type ClimbEntry, type UserProfile } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

type ActivityType = 'verified' | 'approved' | 'milestone' | 'pb';

interface ActivityItem {
  id: string;
  type: ActivityType;
  userId: string;
  userName: string;
  userAvatar: string;
  climbId?: string;
  climbName?: string;
  timeFormatted?: string;
  wattPerKg?: number;
  rank?: number;
  detail: string;
  timestamp: string;
  timestampMs: number;
}

function avatarColors(initials: string): string {
  const palette = [
    'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'bg-red-500/20 text-red-400 border-red-500/30',
    'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'bg-green-500/20 text-green-400 border-green-500/30',
    'bg-pink-500/20 text-pink-400 border-pink-500/30',
    'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  ];
  const idx = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % palette.length;
  return palette[idx];
}

const TYPE_CONFIG: Record<ActivityType, { icon: string; label: string; accent: string; bg: string }> = {
  verified: { icon: 'CheckBadgeIcon', label: 'Verificato', accent: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
  approved: { icon: 'ShieldCheckIcon', label: 'Approvato', accent: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  milestone: { icon: 'StarIcon', label: 'Traguardo', accent: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
  pb: { icon: 'BoltIcon', label: 'Nuovo PR', accent: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
};

function buildActivityFeed(entries: ClimbEntry[], users: UserProfile[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  const baseMs = Date.now();

  const verifiedEntries = entries.filter((e) => e.importedFromStrava && e.status === 'approved');
  verifiedEntries.slice(0, 10).forEach((entry, i) => {
    const climb = CLIMBS.find((c) => c.id === entry.climbId);
    const offsetMs = i * 3_600_000 + Math.floor(i * 1.7) * 600_000;
    items.push({
      id: `verified-${entry.id}`,
      type: 'verified',
      userId: entry.userId,
      userName: entry.userName,
      userAvatar: entry.userAvatar,
      climbId: entry.climbId,
      climbName: climb?.name,
      timeFormatted: entry.timeFormatted,
      wattPerKg: entry.wattPerKg,
      detail: `Salita verificata via Strava su ${climb?.name ?? ''} in ${entry.timeFormatted}`,
      timestamp: entry.date,
      timestampMs: baseMs - offsetMs,
    });
  });

  const manualApproved = entries.filter((e) => !e.importedFromStrava && e.status === 'approved');
  manualApproved.forEach((entry, i) => {
    const climb = CLIMBS.find((c) => c.id === entry.climbId);
    const offsetMs = (i + 2) * 5_400_000;
    items.push({
      id: `approved-${entry.id}`,
      type: 'approved',
      userId: entry.userId,
      userName: entry.userName,
      userAvatar: entry.userAvatar,
      climbId: entry.climbId,
      climbName: climb?.name,
      timeFormatted: entry.timeFormatted,
      detail: `Tempo approvato dall'admin su ${climb?.name ?? ''} — ${entry.timeFormatted}`,
      timestamp: entry.date,
      timestampMs: baseMs - offsetMs,
    });
  });

  const activeUsers = users.filter((u) => u.status === 'active');
  activeUsers.slice(0, 6).forEach((u, i) => {
    const userEntries = entries.filter((e) => e.userId === u.id && e.status === 'approved');
    const count = new Set(userEntries.map((e) => e.climbId)).size;
    if (count === 0) return;
    const offsetMs = (i + 1) * 7_200_000 + i * 900_000;
    items.push({
      id: `milestone-${u.id}-${count}`,
      type: 'milestone',
      userId: u.id,
      userName: u.name,
      userAvatar: u.avatar,
      detail: `Ha completato ${count} salite del circuito IA Climb`,
      timestamp: u.joinedAt,
      timestampMs: baseMs - offsetMs,
    });
  });

  const pbMap: Record<string, ClimbEntry> = {};
  entries.filter((e) => e.status === 'approved').forEach((entry) => {
    const key = `${entry.userId}-${entry.climbId}`;
    if (!pbMap[key] || entry.timeSeconds < pbMap[key].timeSeconds) {
      pbMap[key] = entry;
    }
  });
  Object.values(pbMap).slice(0, 12).forEach((entry, i) => {
    const climb = CLIMBS.find((c) => c.id === entry.climbId);
    const allForClimb = entries.filter((e) => e.climbId === entry.climbId && e.status === 'approved').sort((a, b) => a.timeSeconds - b.timeSeconds);
    const rank = allForClimb.findIndex((e) => e.userId === entry.userId) + 1;
    const offsetMs = (i + 1) * 2_700_000 + i * 480_000;
    items.push({
      id: `pb-${entry.id}`,
      type: 'pb',
      userId: entry.userId,
      userName: entry.userName,
      userAvatar: entry.userAvatar,
      climbId: entry.climbId,
      climbName: climb?.name,
      timeFormatted: entry.timeFormatted,
      wattPerKg: entry.wattPerKg,
      rank,
      detail: `Nuovo record personale su ${climb?.name ?? ''} — ${entry.timeFormatted}`,
      timestamp: entry.date,
      timestampMs: baseMs - offsetMs,
    });
  });

  return items.sort((a, b) => b.timestampMs - a.timestampMs);
}

function relativeTime(ms: number): string {
  const now = Date.now();
  const diff = now - ms;
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (mins < 60) return `${mins}m fa`;
  if (hours < 24) return `${hours}h fa`;
  return `${days}g fa`;
}

const ALL_TYPES: ActivityType[] = ['verified', 'approved', 'milestone', 'pb'];
const FILTER_LABELS: Record<string, string> = {
  all: 'Tutto', verified: 'Verificati', approved: 'Approvati', milestone: 'Traguardi', pb: 'Nuovi PR',
};

export default function ActivityFeedPage() {
  const [filter, setFilter] = useState<'all' | ActivityType>('all');
  const [allItems, setAllItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [entries, users] = await Promise.all([entryService.getAll(), userService.getAll()]);
        setAllItems(buildActivityFeed(entries, users));
      } catch {
        setAllItems([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = filter === 'all' ? allItems : allItems.filter((i) => i.type === filter);

  const counts = {
    verified: allItems.filter((i) => i.type === 'verified').length,
    approved: allItems.filter((i) => i.type === 'approved').length,
    milestone: allItems.filter((i) => i.type === 'milestone').length,
    pb: allItems.filter((i) => i.type === 'pb').length,
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Icon name="BellAlertIcon" size={18} className="text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground font-sans">Attività del Club</h1>
          </div>
          <p className="text-muted-foreground text-sm ml-12">
            Salite verificate, approvazioni, traguardi e nuovi record personali
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
          {ALL_TYPES.map((type) => {
            const cfg = TYPE_CONFIG[type];
            return (
              <div
                key={type}
                className={`rounded-xl border p-3.5 flex flex-col gap-1 cursor-pointer transition-all duration-150 ${
                  filter === type ? `${cfg.bg} border-opacity-60` : 'bg-card border-border hover:border-muted-foreground/30'
                }`}
                onClick={() => setFilter(filter === type ? 'all' : type)}
              >
                <div className="flex items-center gap-2">
                  <Icon name={cfg.icon as 'StarIcon'} size={14} className={cfg.accent} />
                  <span className="text-xs text-muted-foreground font-medium">{cfg.label}</span>
                </div>
                <span className={`text-2xl font-bold font-mono ${cfg.accent}`}>{counts[type]}</span>
              </div>
            );
          })}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {(['all', ...ALL_TYPES] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border whitespace-nowrap transition-all ${
                filter === f ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>

        {/* Feed */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Icon name="BellAlertIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nessuna attività trovata</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => {
              const cfg = TYPE_CONFIG[item.type];
              const colorClass = avatarColors(item.userAvatar);
              return (
                <div key={item.id} className="bg-card border border-border rounded-xl p-4 flex gap-4 hover:border-border/80 transition-all">
                  <div className={`w-9 h-9 rounded-full border flex items-center justify-center text-xs font-bold flex-shrink-0 ${colorClass}`}>
                    {item.userAvatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link href={`/member/${item.userId}`} className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                            {item.userName}
                          </Link>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.accent}`}>
                            <Icon name={cfg.icon as 'StarIcon'} size={10} />
                            {cfg.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.detail}</p>
                        {item.timeFormatted && (
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="font-mono-nums text-xs font-bold text-accent">{item.timeFormatted}</span>
                            {item.wattPerKg && <span className="text-xs text-muted-foreground">{item.wattPerKg.toFixed(1)} W/kg</span>}
                            {item.rank && item.rank <= 3 && (
                              <span className={`text-xs font-bold ${item.rank === 1 ? 'text-yellow-400' : item.rank === 2 ? 'text-gray-300' : 'text-amber-600'}`}>
                                #{item.rank}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">{relativeTime(item.timestampMs)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
